// สถิติรวมของผู้เล่นทั้งหมด (docs/PLAN.md เฟส 4)

import { useEffect, useState } from "react";
import { GROUPS } from "../../data/groups";
import { DEATH_CAUSE_TH, formatAge } from "../../engine/text";
import { DEATH_CAUSES, type StatsResponse } from "../../shared/stats";
import { fetchStats } from "../api";
import { fmt } from "../names";

interface Props {
  onBack: () => void;
}

const pct = (n: number, d: number) => Math.round((n / Math.max(1, d)) * 100);

export function GlobalStatsScreen({ onBack }: Props) {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats().then(setStats, (e: Error) => setError(e.message));
  }, []);

  const g = stats?.global;
  const groups = stats ? [...stats.groups].sort((a, b) => b.lives - a.lives) : [];
  const groupName = (id: string) => GROUPS.find((x) => x.id === id)?.nameTh ?? id;

  return (
    <main className="screen">
      <section className="card">
        <h1 style={{ fontSize: "1.4rem" }}>สถิติรวมของผู้เล่นทั้งหมด</h1>
        <p className="small muted">นับทุกชีวิตที่เล่นจบ อัปเดตทุกราว 1 นาที</p>
      </section>

      {error && <p className="error">{error}</p>}
      {!stats && !error && <p className="muted">กำลังโหลด…</p>}

      {g && g.lives === 0 && <p className="muted">ยังไม่มีใครเล่นจบสักชีวิต มาเป็นคนแรกกัน</p>}

      {g && g.lives > 0 && (
        <>
          <section className="summary-big">
            <div className="card">
              <span className="muted small">ชีวิตทั้งหมด</span>
              <b>{fmt(g.lives)}</b>
            </div>
            <div className="card">
              <span className="muted small">รอดจนโต</span>
              <b>{pct(g.adults, g.lives)}%</b>
            </div>
            <div className="card">
              <span className="muted small">ลูกหลานรวม</span>
              <b>{fmt(g.offspring)}</b>
            </div>
            <div className="card">
              <span className="muted small">อายุเฉลี่ย</span>
              <b>{formatAge(g.ageDays / g.lives)}</b>
            </div>
          </section>

          <section className="card">
            <p>
              เดินทางรวมกัน <b>{fmt(Math.round(g.km))}</b> กม.
              {g.km >= 40075 && ` (รอบโลก ${fmt(Math.floor(g.km / 40075))} รอบ)`}
              {g.maxGeneration > 1 && (
                <>
                  <br />
                  สายตระกูลที่ยาวที่สุด <b>{fmt(g.maxGeneration)}</b> รุ่น
                </>
              )}
            </p>
          </section>

          <section className="card">
            <h2 style={{ fontSize: "1.05rem", marginBottom: 8 }}>จากไปเพราะอะไร</h2>
            <ul className="bars">
              {DEATH_CAUSES.filter((c) => g.causes[c] > 0)
                .sort((a, b) => g.causes[b] - g.causes[a])
                .map((c) => (
                  <li key={c}>
                    <span className="small">{DEATH_CAUSE_TH[c]}</span>
                    <span className="bar-track">
                      <span className="bar-fill" style={{ width: `${pct(g.causes[c], g.lives)}%` }} />
                    </span>
                    <span className="small muted">{pct(g.causes[c], g.lives)}%</span>
                  </li>
                ))}
            </ul>
          </section>

          <section className="card">
            <h2 style={{ fontSize: "1.05rem", marginBottom: 8 }}>แยกตามกลุ่มสัตว์</h2>
            <ul className="bars">
              {groups.map((x) => (
                <li key={x.groupId}>
                  <span className="small">{groupName(x.groupId)}</span>
                  <span className="bar-track">
                    <span className="bar-fill" style={{ width: `${pct(x.lives, groups[0].lives)}%` }} />
                  </span>
                  <span className="small muted">
                    {fmt(x.lives)} · โต {pct(x.adults, x.lives)}%
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <div className="actions">
        <button className="btn block" onClick={onBack}>
          กลับหน้าแรก
        </button>
      </div>
    </main>
  );
}
